import { IAdminUserRoleData } from 'types/administration/userAdministration/IAdminUserRole';
import { convertFromIQueryResponseToIPageData, IPageData } from 'types/rest/IPageData';
import {
  convertToApiQuery,
  IAdminUserData,
  IAdminUserEditRequest,
  IAdminUserResponse,
  IAdminUsersQuery,
} from '../../types/administration/userAdministration/IAdminUser';
import { BaseAPI } from '../BaseApi';
import { eAPITags } from '../eAPITags';
import { UUID } from '../../types/shared/UUID';
import { SelfUpdateAdminRequest } from '../../types/userProfile/SelfUpdateAdminRequest';
import { IQueryResponse } from '../../types/rest/IQueryResponse';

export const AdminUsersApi = BaseAPI.injectEndpoints({
  endpoints: (build) => ({
    listPagedAdminUsers: build.query<IPageData<IAdminUserData>, IAdminUsersQuery>({
      query: (adminUsersQuery) => {
        return {
          url: `/timber/api/internal/users/admins`,
          params: convertToApiQuery(adminUsersQuery),
        };
      },
      providesTags: (result) => [
        ...(result?.content ?? []).map(({ id }) => ({ type: eAPITags.AdminUsersData, id })),
        { type: eAPITags.AdminUsersData, id: 'LIST' },
      ],
      transformResponse: (baseReturnValue: IQueryResponse<IAdminUserResponse>) => {
        return convertFromIQueryResponseToIPageData(baseReturnValue, (data) => data);
      },
    }),
    getAdminUser: build.query<IAdminUserData, UUID>({
      query: (id) => `/timber/api/internal/users/admins/${id}`,
      providesTags: (result, error, id) => [{ type: eAPITags.AdminUsersData, id }],
    }),
    getSelfAdminUser: build.query<IAdminUserData, void>({
      query: () => '/timber/api/internal/users/admins/self',
      providesTags: (result) => [{ type: eAPITags.AdminUsersData, id: result?.id }],
    }),
    updateSelfAdminUser: build.mutation<IAdminUserData, SelfUpdateAdminRequest>({
      query: (body) => ({
        url: `/timber/api/internal/users/admins/self`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result) => [
        { type: eAPITags.AdminUsersData, id: result?.id },
        { type: eAPITags.AdminUsersData, id: 'LIST' },
        // Invalidate all internal contacts queries to show updated external email immediately
        { type: eAPITags.OrderInternalContacts, id: 'LIST' },
      ],
    }),
    listAdminUserRoles: build.query<IAdminUserRoleData[], undefined | { include: 'users' }>({
      query: (params) => ({ url: '/timber/api/internal/auth/roles', params }),
      providesTags: (result) => [
        ...(result ?? []).map(({ id }) => ({ type: eAPITags.AdminUserRoles, id })),
        { type: eAPITags.AdminUserRoles, id: 'LIST' },
      ],
    }),
    updateAdminUser: build.mutation<IAdminUserData, IAdminUserEditRequest>({
      query: (body) => ({
        url: `/timber/api/internal/users/admins/${body.id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: eAPITags.AdminUsersData, id },
        { type: eAPITags.AdminUsersData, id: 'LIST' },
      ],
    }),
    updateAdminUserProfile: build.mutation<IAdminUserData, IAdminUserEditRequest>({
      query: (body) => ({
        url: `/timber/api/internal/users/admins/${body.id}/profile`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: eAPITags.AdminUsersData, id },
        { type: eAPITags.AdminUsersData, id: 'LIST' },
        // Invalidate all internal contacts queries to show updated external email immediately
        { type: eAPITags.OrderInternalContacts, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useListPagedAdminUsersQuery,
  useListAdminUserRolesQuery,
  useGetAdminUserQuery,
  useGetSelfAdminUserQuery,
  useUpdateAdminUserMutation,
  useUpdateAdminUserProfileMutation,
  useUpdateSelfAdminUserMutation,
  usePrefetch,
} = AdminUsersApi;
